import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search as SearchIcon, X, Shirt, Box, User, Sparkles, Loader, ChevronRight, Package
} from 'lucide-react';
import { searchMarketplacePieces } from '../utils/marketplaceQueries';
import { supabase } from '../lib/supabase';
import { normalizePieces } from '../utils/pieceNormalizer';
import { useWallet } from '../contexts/WalletContext';
import VerifiedBadge from './VerifiedBadge';

const SearchModal = ({ isOpen, onClose, onOpenPieceDetail }) => {
  const navigate = useNavigate();
  const { publicKey } = useWallet();
  const searchInputRef = useRef(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  
  // Results
  const [pieces, setPieces] = useState([]);
  const [capsules, setCapsules] = useState([]);
  const [creators, setCreators] = useState([]);
  
  // Counts
  const [piecesTotal, setPiecesTotal] = useState(0);
  const [capsulesTotal, setCapsulesTotal] = useState(0);
  const [creatorsTotal, setCreatorsTotal] = useState(0);

  // Categories configuration
  const categories = [
    { id: 'all', label: 'All', icon: null },
    { id: 'pieces', label: 'Pieces', icon: Shirt },
    { id: 'capsules', label: 'Capsules', icon: Box },
    { id: 'creators', label: 'Wallets', icon: User },
  ];

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        performSearch(searchQuery);
      } else {
        clearResults();
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const clearResults = () => {
    setPieces([]);
    setCapsules([]);
    setCreators([]);
    setPiecesTotal(0);
    setCapsulesTotal(0);
    setCreatorsTotal(0);
  };

  // Group pieces by master_id ONLY - don't group by name
  const groupPiecesByMaster = (pieces) => {
    const grouped = {};
    const seenIds = new Set();
    
    pieces.forEach(piece => {
      // Skip if we've already seen this exact piece ID (deduplication)
      if (seenIds.has(piece.id)) {
        return;
      }
      seenIds.add(piece.id);
      
      // Create group key:
      // ONLY group pieces if they have a master_id
      // Otherwise, treat each piece as unique
      let groupKey;
      
      if (piece.master_id) {
        // Group by master_id for proper editions
        groupKey = `master_${piece.master_id}`;
      } else {
        // Treat each piece without master_id as unique
        groupKey = `id_${piece.id}`;
      }
      
      if (!grouped[groupKey]) {
        // First time seeing this group
        // Get quantity from piece (could be quantity, total_quantity, or default to 1)
        const pieceQty = parseInt(piece.quantity || piece.total_quantity || piece.totalQuantity || 1);
        
        grouped[groupKey] = {
          ...piece,
          editionCount: piece.master_id ? pieceQty : 1, // Only show quantity if it's a master piece
          editions: [piece],
          lowestPrice: piece.lowestPrice || piece.price || piece.listing_price || null,
          // Preserve purchase price information
          last_sale_price_lamports: piece.last_sale_price_lamports,
          holder_wallet: piece.holder_wallet
        };
      } else {
        // Another edition in the same group (only happens with master_id)
        const pieceQty = parseInt(piece.quantity || piece.total_quantity || piece.totalQuantity || 1);
        
        grouped[groupKey].editions.push(piece);
        grouped[groupKey].editionCount += pieceQty;
        
        // Update to lowest price
        const currentPrice = piece.lowestPrice || piece.price || piece.listing_price;
        if (currentPrice && (!grouped[groupKey].lowestPrice || currentPrice < grouped[groupKey].lowestPrice)) {
          grouped[groupKey].lowestPrice = currentPrice;
        }
        
        // Prefer "listed" status
        const currentStatus = piece.listing_status || piece.listingStatus;
        const groupStatus = grouped[groupKey].listing_status || grouped[groupKey].listingStatus;
        
        if (currentStatus === 'listed' && groupStatus !== 'listed') {
          grouped[groupKey].listing_status = 'listed';
          grouped[groupKey].listingStatus = 'listed';
        }
      }
    });
    
    return Object.values(grouped);
  };

  const performSearch = async (query) => {
    if (!query.trim()) return;

    setIsLoading(true);
    
    try {
      // Search pieces (limit to 50 to account for editions, then group and display top 6)
      // Using higher limit to ensure we get enough unique groups after grouping
      const piecesResult = await searchMarketplacePieces(query, 0, 50);
      // Normalize pieces to ensure consistent field names including thumbnailUrl
      const normalizedPieces = normalizePieces(piecesResult.pieces || []);
      
      // Group pieces by master to show editions as one card
      const groupedPieces = groupPiecesByMaster(normalizedPieces);
      
      // Limit to 6 groups for display
      setPieces(groupedPieces.slice(0, 6));
      setPiecesTotal(piecesResult.total || 0);

      // Search capsules (limit to 6 for modal, use planned count for performance)
      const { data: capsulesData, count: capsulesCount } = await supabase
        .from('capsules')
        .select(`
          *,
          creators (
            id,
            wallet,
            handle,
            is_verified,
            verification_type,
            profile_picture_url,
            banner_url,
            bio,
            social_links,
            total_pieces_created
          )
        `, { count: 'planned' })
        .or(`name.ilike.%${query}%,description.ilike.%${query}%`)
        .order('created_at', { ascending: false })
        .limit(6);
      
      // Normalize capsule data to ensure banner field is set
      const normalizedCapsules = (capsulesData || []).map(capsule => {
        // Get banner URL - check banner_url first, then fall back to banner field
        let bannerUrl = capsule.banner_url || capsule.banner || null;
        
        // If old system with banner_bucket/banner_key
        if (!bannerUrl && capsule.banner_bucket && capsule.banner_key) {
          const { data: bannerData } = supabase.storage
            .from(capsule.banner_bucket)
            .getPublicUrl(capsule.banner_key);
          bannerUrl = bannerData?.publicUrl;
        }
        
        return {
          ...capsule,
          banner: bannerUrl, // Normalize to 'banner' for UI
          image: bannerUrl   // Also set 'image' as fallback
        };
      });
      
      setCapsules(normalizedCapsules);
      setCapsulesTotal(capsulesCount || 0);

      // Search creators (limit to 6 for modal, use planned count for performance)
      const { data: creatorsData, count: creatorsCount } = await supabase
        .from('creators')
        .select('*', { count: 'planned' })
        .or(`handle.ilike.%${query}%,bio.ilike.%${query}%,wallet.ilike.%${query}%`)
        .order('total_pieces_created', { ascending: false })
        .limit(6);
      
      setCreators(creatorsData || []);
      setCreatorsTotal(creatorsCount || 0);

    } catch (error) {
      console.error('Error performing search:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePieceClick = async (piece) => {
    console.log('🔍 Opening piece detail modal for:', piece);
    
    // Close the search modal immediately
    onClose();
    
    try {
      // Fetch complete piece data from database with creator info
      const { data: pieceData, error } = await supabase
        .from('pieces')
        .select(`
          *,
          social_links,
          creators (
            id,
            wallet,
            handle,
            is_verified,
            verification_type,
            profile_picture_url,
            banner_url,
            bio,
            social_links,
            total_pieces_created
          )
        `)
        .eq('id', piece.id)
        .maybeSingle();

      if (error || !pieceData) {
        console.error('Error fetching piece:', error);
        onOpenPieceDetail(piece);
        return;
      }

      console.log('📦 Fetched piece with creator data:', pieceData);
      console.log('📊 Piece quantity info:', {
        quantity: pieceData.quantity,
        total_quantity: pieceData.total_quantity,
        quantityType: typeof pieceData.quantity,
        totalQuantityType: typeof pieceData.total_quantity
      });

      // Normalize the piece data
      const normalizedItem = normalizePieces([pieceData])[0];
      
      console.log('📦 Normalized piece data:', normalizedItem);
      console.log('📊 Normalized quantity info:', {
        quantity: normalizedItem.quantity,
        total_quantity: normalizedItem.total_quantity,
        totalQuantity: normalizedItem.totalQuantity
      });
      
      // Open piece detail modal via callback
      onOpenPieceDetail(normalizedItem);
    } catch (error) {
      console.error('Error in handlePieceClick:', error);
      onOpenPieceDetail(piece);
    }
  };

  const handleCapsuleClick = (capsuleId) => {
    navigate(`/capsule/${capsuleId}`);
    onClose();
  };

  const handleCreatorClick = (creator) => {
    // Use handle if available, otherwise use wallet address
    const identifier = creator.handle || creator.wallet || creator.id;
    console.log('🔍 Navigating to creator profile:', { 
      creator, 
      identifier,
      usingHandle: !!creator.handle,
      usingWallet: !creator.handle && !!creator.wallet,
      usingId: !creator.handle && !creator.wallet
    });
    navigate(`/creator/${identifier}`);
    onClose();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    clearResults();
    searchInputRef.current?.focus();
  };

  if (!isOpen) return null;

  const totalResults = piecesTotal + capsulesTotal + creatorsTotal;
  const hasResults = pieces.length > 0 || capsules.length > 0 || creators.length > 0;

  // Get results based on active category
  const getDisplayResults = () => {
    switch (activeCategory) {
      case 'pieces':
        return { showPieces: true, showCapsules: false, showCreators: false };
      case 'capsules':
        return { showPieces: false, showCapsules: true, showCreators: false };
      case 'creators':
        return { showPieces: false, showCapsules: false, showCreators: true };
      default:
        return { showPieces: true, showCapsules: true, showCreators: true };
    }
  };

  const { showPieces, showCapsules, showCreators } = getDisplayResults();

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200]"
        onClick={onClose}
      />
      
      {/* Modal - Larger size */}
      <div className="fixed top-16 left-1/2 transform -translate-x-1/2 z-[201] w-[900px] max-w-[95vw]">
        <div 
          className="bg-[#0a0a0a] border border-white/20 rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search Input */}
          <div className="p-4 border-b border-white/10">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search Outsera"
                className="w-full pl-10 pr-10 py-3 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-white/40 text-base"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Category Tabs - Always visible */}
          <div className="flex border-b border-white/10 px-4">
            {categories.map((category) => {
              const Icon = category.icon;
              const count = 
                category.id === 'pieces' ? piecesTotal :
                category.id === 'capsules' ? capsulesTotal :
                category.id === 'creators' ? creatorsTotal : totalResults;

              return (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeCategory === category.id
                      ? 'border-white text-white'
                      : 'border-transparent text-gray-400 hover:text-white'
                  }`}
                >
                  {Icon && <Icon size={16} />}
                  {category.label}
                  {searchQuery && count > 0 && category.id !== 'all' && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      activeCategory === category.id ? 'bg-white/20' : 'bg-white/10'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Results Container - Fixed height */}
          <div className="h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
            {!searchQuery ? (
              <div className="flex flex-col items-center justify-center h-full px-6">
                <SearchIcon className="mb-3 text-gray-600" size={40} />
                <p className="text-gray-400 text-base text-center mb-2">
                  {activeCategory === 'all' && 'Search for pieces, capsules, and wallets'}
                  {activeCategory === 'pieces' && 'Search for pieces'}
                  {activeCategory === 'capsules' && 'Search for capsules'}
                  {activeCategory === 'creators' && 'Search for wallets'}
                </p>
                <p className="text-gray-500 text-sm text-center">
                  Type to start searching
                </p>
              </div>
            ) : isLoading ? (
              <div className="flex flex-col items-center justify-center h-full">
                <Loader className="animate-spin text-white mb-3" size={28} />
                <p className="text-gray-400 text-sm">Searching...</p>
              </div>
            ) : !hasResults ? (
              <div className="flex flex-col items-center justify-center h-full px-6">
                <SearchIcon className="mb-3 text-gray-600" size={40} />
                <h3 className="text-base font-semibold text-gray-400 mb-1">
                  No {activeCategory === 'all' ? 'results' : activeCategory === 'pieces' ? 'pieces' : activeCategory === 'capsules' ? 'capsules' : 'wallets'} found
                </h3>
                <p className="text-gray-500 text-sm text-center">
                  Try searching with different keywords
                  {activeCategory !== 'all' && ' or switch to another category'}
                </p>
              </div>
            ) : (
              <div className="p-2">
                {/* Pieces */}
                {showPieces && pieces.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between px-3 py-2">
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Pieces {piecesTotal > 0 && <span className="text-gray-500">({piecesTotal.toLocaleString()})</span>}
                      </div>
                      {piecesTotal > pieces.length && (
                        <button
                          onClick={() => {
                            navigate(`/explore?search=${encodeURIComponent(searchQuery)}`);
                            onClose();
                          }}
                          className="text-xs text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1"
                        >
                          View All <ChevronRight size={12} />
                        </button>
                      )}
                    </div>
                    <div className="space-y-1">
                      {pieces.map((piece) => (
                        <button
                          key={piece.id}
                          onClick={() => handlePieceClick(piece)}
                          className="w-full flex items-center gap-4 p-4 hover:bg-white/5 rounded-lg transition-colors text-left group cursor-pointer"
                        >
                          <img
                            src={piece.thumbnailUrl || piece.thumbnail_url || piece.thumbnail?.dataUrl || '/images/piece-placeholder.png'}
                            alt={piece.name}
                            className="w-16 h-16 rounded-lg object-cover border border-white/10"
                            onError={(e) => {
                              e.target.src = '/images/piece-placeholder.png';
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="text-white text-base font-medium truncate group-hover:text-gray-200">
                                {piece.name}
                              </h4>
                              {/* Edition Count Badge - only show if more than 1 */}
                              {piece.editionCount > 1 && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400 flex-shrink-0">
                                  {piece.editionCount} pieces
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              {/* Listing Status Badge */}
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                piece.listing_status === 'listed' || piece.listingStatus === 'listed'
                                  ? 'bg-green-500/20 text-green-400'
                                  : piece.listing_status === 'draft' || piece.listingStatus === 'draft'
                                  ? 'bg-gray-500/20 text-gray-400'
                                  : 'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {piece.listing_status === 'listed' || piece.listingStatus === 'listed' ? 'Listed' : 
                                 piece.listing_status === 'draft' || piece.listingStatus === 'draft' ? 'Draft' : 'Unlisted'}
                              </span>
                              {/* Price */}
                              {(() => {
                                const isOwnedByCurrentUser = publicKey && piece.holder_wallet === publicKey.toString();
                                const isListed = piece.listing_status === 'listed' || piece.listingStatus === 'listed';
                                
                                // If user owns this piece
                                if (isOwnedByCurrentUser) {
                                  // If they've listed it, show THEIR listing price
                                  if (isListed && (piece.listing_price || piece.listingPrice)) {
                                    const listingPrice = piece.listing_price || piece.listingPrice;
                                    const formattedPrice = typeof listingPrice === 'number' 
                                      ? listingPrice.toFixed(4).replace(/\.?0+$/, '')
                                      : listingPrice;
                                    return (
                                      <span className="text-green-400 font-semibold text-sm">
                                        {formattedPrice} SOL
                                      </span>
                                    );
                                  }
                                  
                                  // If not listed, show purchase price
                                  if (!isListed && piece.last_sale_price_lamports) {
                                    const priceSOL = piece.last_sale_price_lamports / 1_000_000_000;
                                    const formattedPrice = priceSOL.toFixed(4).replace(/\.?0+$/, '');
                                    return (
                                      <span className="text-green-400 font-semibold text-sm">
                                        {formattedPrice} SOL
                                      </span>
                                    );
                                  }
                                }
                                
                                // For pieces not owned by user, show lowest available price
                                if (piece.lowestPrice) {
                                  const formattedPrice = typeof piece.lowestPrice === 'number'
                                    ? piece.lowestPrice.toFixed(4).replace(/\.?0+$/, '')
                                    : piece.lowestPrice;
                                  return (
                                    <span className="text-green-400 font-semibold text-sm">
                                      {piece.editionCount > 1 ? 'from ' : ''}{formattedPrice} SOL
                                    </span>
                                  );
                                }
                                
                                return null;
                              })()}
                            </div>
                          </div>
                          <ChevronRight size={18} className="text-gray-600 group-hover:text-gray-400 flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Capsules */}
                {showCapsules && capsules.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between px-3 py-2">
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Capsules {capsulesTotal > 0 && <span className="text-gray-500">({capsulesTotal.toLocaleString()})</span>}
                      </div>
                      {capsulesTotal > capsules.length && (
                        <button
                          onClick={() => {
                            navigate(`/explore?search=${encodeURIComponent(searchQuery)}&tab=capsules`);
                            onClose();
                          }}
                          className="text-xs text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1"
                        >
                          View All <ChevronRight size={12} />
                        </button>
                      )}
                    </div>
                    <div className="space-y-1">
                      {capsules.map((capsule) => (
                        <button
                          key={capsule.id}
                          onClick={() => handleCapsuleClick(capsule.id)}
                          className="w-full flex items-center gap-4 p-4 hover:bg-white/5 rounded-lg transition-colors text-left group cursor-pointer"
                        >
                          {/* Banner Image - wider aspect ratio */}
                          <div className="w-24 h-16 rounded-lg overflow-hidden border border-white/10 flex-shrink-0 bg-[#2a2a2a]">
                            {capsule.banner || capsule.image ? (
                              <img
                                src={capsule.banner || capsule.image}
                                alt={capsule.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src = 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=300&h=200&fit=crop';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-purple-900/50 to-pink-900/50 flex items-center justify-center">
                                <Package size={24} className="text-white/30" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white text-base font-medium truncate group-hover:text-gray-200">
                              {capsule.name}
                            </h4>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-gray-400 text-sm">
                                {capsule.piece_count || capsule.pieceCount || 0} {(capsule.piece_count || capsule.pieceCount || 0) === 1 ? 'piece' : 'pieces'}
                              </span>
                              {capsule.price && (
                                <>
                                  <span className="text-gray-600">•</span>
                                  <span className="text-green-400 font-semibold text-sm">
                                    {capsule.price} SOL avg
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <ChevronRight size={18} className="text-gray-600 group-hover:text-gray-400 flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Creators/Wallets */}
                {showCreators && creators.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between px-3 py-2">
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Wallets {creatorsTotal > 0 && <span className="text-gray-500">({creatorsTotal.toLocaleString()})</span>}
                      </div>
                      {creatorsTotal > creators.length && (
                        <button
                          onClick={() => {
                            // Navigate to explore page with creators search
                            // Note: This could be enhanced to have a dedicated creators page
                            navigate(`/explore?search=${encodeURIComponent(searchQuery)}`);
                            onClose();
                          }}
                          className="text-xs text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1"
                        >
                          View All <ChevronRight size={12} />
                        </button>
                      )}
                    </div>
                    <div className="space-y-1">
                      {creators.map((creator) => (
                        <button
                          key={creator.id}
                          onClick={() => handleCreatorClick(creator)}
                          className="w-full flex items-center gap-4 p-4 hover:bg-white/5 rounded-lg transition-colors text-left group cursor-pointer"
                        >
                          {creator.profile_picture_url ? (
                            <img
                              src={creator.profile_picture_url}
                              alt={creator.handle}
                              className="w-14 h-14 rounded-full object-cover border border-white/10"
                            />
                          ) : (
                            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center border border-white/10">
                              <User size={24} className="text-white" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="text-white text-base font-medium truncate group-hover:text-gray-200">
                                @{creator.handle}
                              </h4>
                              {creator.is_verified && (
                                <VerifiedBadge 
                                  size="sm"
                                  verificationType={creator.verification_type || 'none'}
                                />
                              )}
                            </div>
                            <p className="text-gray-500 text-sm">
                              {creator.total_pieces_created || 0} pieces
                            </p>
                          </div>
                          <ChevronRight size={18} className="text-gray-600 group-hover:text-gray-400 flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
    </>
  );
};

export default SearchModal;

